import * as React from "react";
import { useBlocker, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowRight, CircleCheck, Eye, FileText, Loader2, Receipt } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Combobox } from "@/components/Combobox";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DateTimePicker } from "@/components/DateTimePicker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { SummaryRow } from "@/components/SummaryRow";
import { AllowancePreviewCard } from "@/components/requests/AllowancePreviewCard";
import { BillsSummaryCard } from "@/components/requests/DetailCard";
import { CurrencyConversionCard } from "@/components/requests/CurrencyConversionCard";
import { DocumentsStep } from "@/components/requests/DocumentsStep";
import { ExpensesStep } from "@/components/requests/ExpensesStep";
import { FinanceNoticeDialog } from "@/components/requests/FinanceNoticeDialog";
import { LinkAdvanceField } from "@/components/requests/LinkAdvanceField";
import { TravelTypePicker } from "@/components/requests/TravelTypePicker";
import { TripLocationFields } from "@/components/requests/TripLocationFields";
import { WizardStepper } from "@/components/requests/WizardStepper";
import { formatDateTime, toIso, toLocalDatetimeInput } from "@/lib/datetime";
import { formatAdvanceAmounts } from "@/lib/advance";
import { tripLocationPayload, validateTripLocation } from "@/lib/trip";
import { cn } from "@/lib/utils";
import {
  ApiError,
  createMyRequest,
  fetchAdvance,
  fetchAllowedCountries,
  fetchFinanceApprovers,
  fetchProfile,
  fetchTravelRequest,
  finalizeMyRequest,
  FinanceApprover,
  previewAllowance,
  resubmitMyRequest,
  saveResubmitEdits,
  setDraftLinkedAdvance,
  TripLocationPayload
} from "../../api";
import {
  Advance,
  AdvanceAmount,
  AllowancePreview,
  AllowedCountry,
  CLAIM_METHOD_LABEL,
  ClaimMethod,
  ExpenseLineItem,
  FxOverride,
  RequestDocument,
  TRAVEL_TYPE_LABEL,
  TravelType
} from "../../types";

interface FormState {
  department: string;
  /** The policy country: the one travelled within for Domestic, the
   *  destination for International (see TripLocationFields). */
  country: string;
  /** The employee's explicit choice, made on the first step - "" until they
   *  pick one. Decides the rate, the country list and which of the
   *  location fields below are asked for and sent. */
  travelType: TravelType | "";
  /** International only. */
  originCountry: string;
  /** Domestic only. */
  startCity: string;
  endCity: string;
  departureAt: string; // datetime-local string
  arrivalAt: string;
  flightDetails: string;
  currency: string;
  currencyAutoSelected: boolean;
  claimMethod: ClaimMethod;
  linkedAdvanceId: string | null;
  /** Who this request routes to at the Finance stage - the requester's own
   *  pick from fetchFinanceApprovers(), required alongside the rest of
   *  Travel Details (see validateStep) since every request eventually needs
   *  someone to decide it there, whether via the normal Manager-approved
   *  path or (no manager on file) straight away. */
  financeApproverId: string;
}

const EMPTY_FORM: FormState = {
  department: "",
  country: "",
  travelType: "",
  originCountry: "",
  startCity: "",
  endCity: "",
  departureAt: "",
  arrivalAt: "",
  flightDetails: "",
  currency: "",
  currencyAutoSelected: true,
  claimMethod: "fixed_allowance",
  linkedAdvanceId: null,
  financeApproverId: ""
};

// Claim Method (Fixed Allowance vs Reimbursement) AND the travel type
// (Domestic vs International) are chosen FIRST, on the same step: together
// they decide the policy rate, which countries are offered, which location
// fields Travel Details asks for, and which Paid advances can be linked. There
// is no standalone Currency step: the overall request currency is fully
// derived from the chosen country (never independently editable - see
// TripLocationFields, whose country picker is restricted to
// fetchAllowedCountries()'s list, so every option always resolves to
// exactly one currency).
// Both claim methods then offer "Advance?" - linking a Paid advance of the
// same travel type auto-fills the upcoming Travel Details step
// (department/country/locations/dates/flight - never amount, which isn't a
// Travel Details field) so the employee doesn't re-type what they already
// gave when raising the advance. Both claim methods: Claim Method → Advance?
// → Travel Details → Bills → Review. For Reimbursement the bills ARE the
// claim (at least one required); for Fixed Allowance the step is optional,
// labelled "Expenses (optional)", and tracking-only - recorded for audit,
// never changing the allowance (see requests_api.py's Expenses section).
// Individual bills carry their OWN currency (see ExpensesStep), independent
// of this request-level one - the request's currency is used only for the
// policy limit/allowance preview, not to constrain bills.
// The Bills step sits at the same position (BILLS_STEP_INDEX) for both, so
// code locates it by position, never by its (claim-method-specific) label.
const BILLS_STEP_INDEX = 4;
function stepsFor(claimMethod: ClaimMethod) {
  return [
    { label: "Claim Method" },
    { label: "Advance?" },
    { label: "Travel Details" },
    {
      label: claimMethod === "reimbursement" ? "Bills" : "Expenses (optional)"
    },
    { label: "Review & Submit" }
  ];
}

/** The where-to fields sent with a request. Callers have already made sure
 *  travelType is set (validateStep blocks leaving the first step without
 *  it). */
function tripPayload(form: FormState): TripLocationPayload {
  return tripLocationPayload(form.travelType as TravelType, form);
}

// A refresh mid-wizard used to lose every in-progress field AND, worse,
// meant continuing past Travel Details created a SECOND server Draft (see
// createDraftForBillsStep) since draftRequestId reset to null - leaving
// the first refresh's abandoned Draft as an orphan the employee had to
// separately discard. Persisting this snapshot to sessionStorage (not
// localStorage - a fresh browser session should start clean, this is only
// meant to survive an accidental reload of the SAME tab/session) closes
// both gaps: reloading restores step/form/expenses AND the already-created
// draftRequestId, so `next()`'s `!draftRequestId` check correctly skips
// creating another one. Cleared on successful submit/finalize and on an
// explicit discard - never meant to outlive one wizard attempt.
const WIZARD_STORAGE_KEY = "asmara.newRequest.draft";

interface WizardSnapshot {
  step: number;
  form: FormState;
  draftRequestId: string | null;
  expenses: ExpenseLineItem[];
  /** Supporting documents added so far - persisted alongside expenses so a
   *  mid-wizard reload restores the Additional Documents tab too. Absent on
   *  snapshots saved before this feature existed; restores as []. */
  documents?: RequestDocument[];
  /** Every currency line of the linked advance (null = none linked). A
   *  snapshot saved before multi-currency advances has no such key and
   *  restores as null - the advance stays linked by id, only the Review
   *  step's figures are missing until it's re-picked. */
  linkedAdvanceAmounts?: AdvanceAmount[] | null;
}

/** True only when every key EMPTY_FORM currently declares is present on
 *  the parsed snapshot's own form object - a snapshot saved by an OLDER
 *  version of this wizard (before a field such as financeApproverId
 *  existed) simply lacks that key entirely. Rejecting the WHOLE snapshot
 *  in that case (not just merging a default onto the missing field) means
 *  a shape-mismatched resume can never silently skip a step whose new
 *  field the employee never actually got a chance to see/fill in - they
 *  land back on a fresh Step 1 instead, guaranteed to hit every current
 *  step in order. A same-shape snapshot (the normal case - reload mid-
 *  wizard on the current code) still resumes exactly as before. */
function isCurrentShape(form: unknown): form is FormState {
  if (!form || typeof form !== "object") return false;
  return Object.keys(EMPTY_FORM).every(
    (key) => key in (form as Record<string, unknown>)
  );
}

function loadWizardSnapshot(): WizardSnapshot | null {
  try {
    const raw = sessionStorage.getItem(WIZARD_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WizardSnapshot;
    if (!isCurrentShape(parsed.form)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveWizardSnapshot(snapshot: WizardSnapshot): void {
  try {
    sessionStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Best-effort - a full/blocked sessionStorage just means this one
    // reload won't be recoverable, never a reason to break the wizard.
  }
}

function clearWizardSnapshot(): void {
  try {
    sessionStorage.removeItem(WIZARD_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export default function NewRequest() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftIdToResume = searchParams.get("draftId");
  // Explicit "start fresh" signal - from My Requests' "New request" button
  // when no Draft exists yet, or from the unfinished-draft reminder's
  // "Start a new one anyway" (see MyRequests.tsx). Without this, a leftover
  // sessionStorage snapshot from an earlier ABANDONED wizard attempt (closed
  // without submitting - there's no explicit Cancel, so nothing else ever
  // clears it) would silently resume, including its already-created
  // draftRequestId, dumping the employee back into the draft they just said
  // they didn't want.
  const startFresh = searchParams.get("fresh") === "1";
  // Set alongside draftIdToResume from MyRequests.tsx's "Edit & resubmit"
  // action on a ResubmitRequested request - UNLIKE a normal Draft resume,
  // this needs full edit parity (every step reachable, not just Bills) and
  // finalizes via resubmitMyRequest (reuses the SAME approval record,
  // routing straight back to whichever stage sent it back) instead of
  // finalizeMyRequest (which would create a brand new one). See the resume
  // effect and submit() below for exactly where this changes behaviour.
  const isResubmit = searchParams.get("resubmit") === "1";

  // Restored once, on first mount, from sessionStorage - but only when NOT
  // resuming an explicit ?draftId= and NOT starting fresh (see startFresh
  // above; ?draftId= is authoritative and fetches from the server instead,
  // see the effect below). Lazy initializers so this read happens exactly
  // once, not on every render.
  const restored = React.useMemo(
    () => (draftIdToResume || startFresh ? null : loadWizardSnapshot()),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  React.useEffect(() => {
    if (startFresh) clearWizardSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [step, setStep] = React.useState(restored?.step ?? 1);
  // Safe to use restored?.form directly - loadWizardSnapshot() above
  // already rejects (returns null for) any snapshot whose form doesn't
  // match FormState's current shape, so `restored` is either null or
  // guaranteed current-shape, never a partial/stale object.
  const [form, setForm] = React.useState<FormState>(
    restored?.form ?? EMPTY_FORM
  );
  const [error, setError] = React.useState<string | null>(null);
  // A validation/submit error (e.g. "Designation, Departure, and Arrival
  // are required.") used to only clear on the NEXT button click - so a
  // user who noticed the message and filled in the missing field still saw
  // it sitting there, stale, until they clicked Next again. Any edit to the
  // form now clears it immediately instead, on the theory that an error
  // tied to the form's old state stops being trustworthy the moment that
  // state changes - re-validation (if still invalid) happens again on the
  // next Next/Submit click as before.
  React.useEffect(() => {
    setError(null);
  }, [form]);
  const [submitting, setSubmitting] = React.useState(false);
  // Confirmed before handleSubmitClick's own branch (straight to submit(),
  // or the Notify Finance popup for a manager-less requester) runs — the
  // Review & Submit step already shows everything being sent, so this is
  // just a last "are you sure" on top of that, not a second review.
  const [confirmSubmitOpen, setConfirmSubmitOpen] = React.useState(false);
  const [resuming, setResuming] = React.useState(!!draftIdToResume);

  // The approver's note on what to fix, captured when resuming a
  // ResubmitRequested request (isResubmit) - shown as a banner so the
  // requester can actually see WHY it came back before editing. Null for a
  // brand-new request or a plain Draft resume (neither has one).
  const [resubmitNote, setResubmitNote] = React.useState<{
    stage: "Manager" | "Finance";
    comment: string;
  } | null>(null);

  // True while a bill on the Expenses step is still being analysed / duplicate-
  // checked (reported up by ExpensesStep) - gates the Next button so a click
  // mid-analysis can't leave the step and discard the in-flight card.
  const [expensesBusy, setExpensesBusy] = React.useState(false);
  // Resubmit only: the Finance Approver the request was first submitted to,
  // shown read-only (it returns to the same approvers), and whether the
  // edits are being saved on leaving Travel Details.
  const [financeApproverNameAtSubmission, setFinanceApproverNameAtSubmission] =
    React.useState<string | null>(null);
  const [savingResubmitEdits, setSavingResubmitEdits] = React.useState(false);
  // Bill cards on the Expenses step not yet saved with "Add bill" - Next
  // would otherwise leave the step and silently drop them.
  const [unaddedBills, setUnaddedBills] = React.useState(0);
  // True while an ALREADY-ADDED bill's inline edit form is open (reported
  // by ExpensesStep) - Next would otherwise leave the step and silently
  // discard the in-progress edit without the employee ever clicking the
  // edit form's own Save.
  const [editingExpense, setEditingExpense] = React.useState(false);

  const [preview, setPreview] = React.useState<AllowancePreview | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);

  // Only countries an admin has actually configured a limit for, under the
  // CURRENT claim method AND travel type - refetched whenever either
  // changes, since Fixed Allowance and Reimbursement, and Domestic and
  // International, can each have different country lists (see
  // db/policy_store.py's list_countries_for). The country picker offers
  // exactly this list, so there is no "not configured for this country"
  // failure mode left for the employee to hit at submission - that's now a
  // configuration gap for an admin to fill, not something this form can
  // produce. Nothing is fetched until a travel type has been chosen.
  const [allowedCountries, setAllowedCountries] = React.useState<
    AllowedCountry[]
  >([]);
  const [allowedCountriesLoading, setAllowedCountriesLoading] =
    React.useState(false);
  const [allowedCountriesFor, setAllowedCountriesFor] = React.useState<
    string | null
  >(null);
  const allowedCountriesKey = `${form.travelType}|${form.claimMethod}`;

  React.useEffect(() => {
    if (!form.travelType) {
      setAllowedCountries([]);
      setAllowedCountriesFor(null);
      setAllowedCountriesLoading(false);
      return;
    }
    let cancelled = false;
    const key = `${form.travelType}|${form.claimMethod}`;
    setAllowedCountriesLoading(true);
    fetchAllowedCountries(form.travelType, form.claimMethod)
      .then((list) => {
        if (!cancelled) {
          setAllowedCountries(list);
          setAllowedCountriesFor(key);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAllowedCountries([]);
          setAllowedCountriesFor(key);
        }
      })
      .finally(() => {
        if (!cancelled) setAllowedCountriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [form.claimMethod, form.travelType]);

  // Set once the Reimbursement path creates its Draft request (on first
  // reaching the bills step) - the bills step and the final submit both
  // need this id. Restored from sessionStorage on reload (see `restored`
  // above) so `next()` sees a non-null draftRequestId and does NOT create
  // a second server Draft for the same in-progress wizard session.
  const [draftRequestId, setDraftRequestId] = React.useState<string | null>(
    restored?.draftRequestId ?? null
  );
  const [expenses, setExpenses] = React.useState<ExpenseLineItem[]>(
    restored?.expenses ?? []
  );
  // Not part of the local wizard snapshot (unlike expenses/documents) - an
  // FX override is saved to the server the instant it's set (see
  // CurrencyConversionCard.tsx), so there's no in-progress state here to
  // lose on a reload; it simply starts empty and is set from whichever Draft
  // fetch/update response most recently carried a fresh one.
  const [fxOverrides, setFxOverrides] = React.useState<FxOverride[]>([]);
  // Same "saved server-side the instant it's set, not part of the wizard
  // snapshot" model as fxOverrides above - a ledger row's chosen
  // settlement currency is now persisted too (setSettlementTarget), so a
  // Draft reload shows the same choices instead of every row reverting to
  // the request's own currency.
  const [settlementTargetByCurrency, setSettlementTargetByCurrency] = React.useState<Record<string, string>>({});
  // Review & Submit step: the eye icon beside "Bills uploaded" opens a plain
  // view-only popup of the uploaded bills (BillsSummaryCard with no
  // onEditExpense - no pencil, and the card itself never offers add/delete
  // regardless) - correcting a bill is still only done from the Bills step
  // itself, not from here.
  const [viewingBills, setViewingBills] = React.useState(false);
  // Supporting documents on the Additional Documents tab of the Bills step -
  // plain attachments, no policy/estimate impact, so (unlike expenses) they
  // never feed the Review figures or AllowancePreviewCard.
  const [documents, setDocuments] = React.useState<RequestDocument[]>(
    restored?.documents ?? []
  );
  // True while a document upload is in flight (reported by DocumentsStep) -
  // gates Next the same way expensesBusy does for a bill analysis.
  const [documentsBusy, setDocumentsBusy] = React.useState(false);
  const [creatingDraft, setCreatingDraft] = React.useState(false);

  // A country chosen (or filled in from a linked advance) earlier may not be
  // offered once the claim method or travel type changes - drop it so it
  // can't be submitted; and keep the read-only currency in step with
  // whichever country is chosen. Skipped once the Draft exists: its country
  // is already saved server-side and the Bills step doesn't edit it, so a
  // resumed Draft must keep showing it even if policy has since moved on.
  // NOT skipped during a resubmit edit (isResubmit) - that needs full edit
  // parity, Travel Details included, so a changed country there should
  // still sync its currency same as a brand new wizard session would.
  React.useEffect(() => {
    if (
      allowedCountriesLoading ||
      allowedCountriesFor !== allowedCountriesKey ||
      (draftRequestId && !isResubmit) ||
      !form.country
    )
      return;
    const match = allowedCountries.find((c) => c.country === form.country);
    if (!match) {
      setForm((f) => ({ ...f, country: "", currency: "" }));
    } else if (match.currency !== form.currency) {
      setForm((f) => ({ ...f, currency: match.currency }));
    }
  }, [
    allowedCountries,
    allowedCountriesLoading,
    allowedCountriesFor,
    allowedCountriesKey,
    draftRequestId,
    isResubmit,
    form.country,
    form.currency
  ]);

  // Only the linked advance's amounts, kept for the Review step's "advance
  // received" / "remaining to settle" figures - never submitted as part of
  // the request itself (form.linkedAdvanceId is the id that's actually
  // sent). Set when LinkAdvanceField reports a pick, or refetched by id
  // when resuming a Draft (which only stores the id, not the amounts).
  const [linkedAdvanceAmounts, setLinkedAdvanceAmounts] = React.useState<
    AdvanceAmount[] | null
  >(restored?.linkedAdvanceAmounts ?? null);
  // The "Did you already receive a travel advance?" toggle shown by
  // LinkAdvanceField - owned here, not by that component, so it survives
  // LinkAdvanceField unmounting when the employee leaves the Advance? step
  // and comes back (it's only rendered while `step === advanceStepIndex`).
  // Lets validateStep block Next on that step while it's on but nothing's
  // been picked yet (toggled on, no linkedAdvanceId), rather than silently
  // letting the employee move on having said yes to an advance they never
  // actually selected.
  const [advanceToggleOn, setAdvanceToggleOn] = React.useState(!!form.linkedAdvanceId);

  // Persist the wizard's in-progress state on every change so a reload
  // (or an accidental tab close) restores exactly where the employee left
  // off, instead of losing every field and - worse - creating a second
  // orphaned server Draft the next time they reach the Bills step (see
  // WIZARD_STORAGE_KEY's comment above and next()'s !draftRequestId
  // check). Skipped while resuming an explicit ?draftId=, which has its
  // own effect below and shouldn't fight with this one over the same key
  // before that fetch has even completed.
  React.useEffect(() => {
    if (draftIdToResume) return;
    saveWizardSnapshot({
      step,
      form,
      draftRequestId,
      expenses,
      documents,
      linkedAdvanceAmounts
    });
  }, [
    draftIdToResume,
    step,
    form,
    draftRequestId,
    expenses,
    documents,
    linkedAdvanceAmounts
  ]);

  const steps = stepsFor(form.claimMethod);

  // Named step indices instead of magic numbers scattered through
  // validateStep/next/the render below. Both claim methods share the same
  // five steps (see stepsFor).
  const isReimbursement = form.claimMethod === "reimbursement";
  const claimMethodStepIndex = 1;
  const advanceStepIndex = 2;
  const travelDetailsStepIndex = 3;
  const billsStepIndex = BILLS_STEP_INDEX;
  const reviewStepIndex = steps.length;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // Both first-step choices reshape what follows, including the country
  // list itself (allowedCountriesKey is travelType+claimMethod together) -
  // the country/currency are cleared outright on either change rather than
  // left for the country-sync effect above to catch, since that effect only
  // acts once the NEW list has finished loading (allowedCountriesFor ===
  // allowedCountriesKey); until then the stale country still satisfies the
  // live-preview effect's canPreview check, which has no such gate, firing
  // a preview for a country never actually chosen under the new claim
  // method - and if that country happens to ALSO be configured for the new
  // claim method (common - the same country often has both a Fixed
  // Allowance and an Actual Amount row), the sync effect's own match check
  // then succeeds and leaves it silently selected, never prompting the
  // employee to re-pick it. A different travel type also changes which
  // locations are asked for and which advances can be linked, so the
  // dependent answers are cleared outright there too.
  const chooseTravelType = (travelType: TravelType) => {
    if (travelType === form.travelType) return;
    releaseLinkedAdvanceOnServer();
    setForm((f) => ({
      ...f,
      travelType,
      country: "",
      currency: "",
      originCountry: "",
      startCity: "",
      endCity: "",
      departureAt: "",
      arrivalAt: "",
      flightDetails: "",
      linkedAdvanceId: null
    }));
    setLinkedAdvanceAmounts(null);
  };

  // An advance only links to a request of its own claim method (and travel
  // type), so switching either drops the link. startCity/endCity/
  // originCountry/departureAt/arrivalAt/flightDetails are cleared too, same
  // as chooseTravelType above - left alone, a Domestic trip's start/end city
  // (or an International trip's origin country), its dates, or its purpose
  // of travel silently carried over from the OLD claim method's session into
  // the new one, still shown as filled-in even though the employee never
  // confirmed those answers apply to this claim method's trip (confirmed
  // live: Fixed Allowance + Domestic -> pick Coimbatore/Chennai -> switch to
  // Actual Amount + Domestic still showed Coimbatore/Chennai, country/
  // currency correctly cleared but the cities were not).
  const chooseClaimMethod = (claimMethod: ClaimMethod) => {
    if (claimMethod === form.claimMethod) return;
    releaseLinkedAdvanceOnServer();
    setForm((f) => ({
      ...f,
      claimMethod,
      country: "",
      currency: "",
      originCountry: "",
      startCity: "",
      endCity: "",
      departureAt: "",
      arrivalAt: "",
      flightDetails: "",
      linkedAdvanceId: null
    }));
    setLinkedAdvanceAmounts(null);
  };

  // Once the request exists on the server (a Draft, or a request being
  // edited for resubmission) it already holds its advance there - when a
  // claim method/travel type change drops the link, release it there
  // straight away too, so the form and the server agree and the advance is
  // linkable again (including straight back onto this request).
  const releaseLinkedAdvanceOnServer = () => {
    if (!draftRequestId || !form.linkedAdvanceId) return;
    setDraftLinkedAdvance(draftRequestId, null).catch(() =>
      toast.error(
        "Couldn't release the previously linked advance - check your connection and try again."
      )
    );
  };

  // Links, swaps or unlinks (advanceId null) the request's advance. Once a
  // Reimbursement Draft exists the server already holds the advance as
  // "linked" (see createDraftForBillsStep), so the change has to be told to
  // the server too - otherwise unlinking here would only hide it: it would
  // stay consumed by the Draft, missing from every picker, and still be
  // attached when the Draft is submitted. Resolves to false when the server
  // refuses, so LinkAdvanceField leaves things as they were.
  const [unlinkingAdvance, setUnlinkingAdvance] = React.useState(false);
  const changeLinkedAdvance = async (
    advanceId: string | null,
    advance?: Advance
  ): Promise<boolean> => {
    // While editing for resubmission, a newly picked advance is only held
    // here - the server links it when the edits are saved (leaving Travel
    // Details, or resubmitting), checked against the EDITED trip and claim
    // method rather than the original submission's. Releasing one still
    // happens straight away.
    const deferToResubmitEdit = isResubmit && advanceId !== null;
    if (draftRequestId && !deferToResubmitEdit) {
      try {
        await setDraftLinkedAdvance(draftRequestId, advanceId);
      } catch (err) {
        // setError alone is not enough here: its only render (the {error &&
        // ...} paragraph) sits inside the Review & Submit step's own block,
        // so a failure picking/unlinking an advance on the Advance? step
        // (step 2) - e.g. after a Draft already exists and its locked-in
        // trip currency doesn't match the newly picked advance's - set this
        // state but showed nothing at all, the dropdown just silently
        // reverting with no visible reason (confirmed live - "I can't
        // select advance" after going back to step 2 from step 4). A toast
        // is visible regardless of which step is showing, same as every
        // other server-call failure in this file (e.g.
        // releaseLinkedAdvanceOnServer just above).
        const message =
          err instanceof ApiError
            ? err.message
            : "Could not change the linked advance - check your connection and try again.";
        toast.error(message);
        setError(message);
        return false;
      }
    }
    setError(null);
    set("linkedAdvanceId", advanceId);
    setLinkedAdvanceAmounts(advance ? advance.amounts : null);
    // Auto-fills Travel Details from the linked advance - never the amount,
    // since that's the advance amount, not what's being claimed. Still
    // freely editable on the next step. (The amount itself is kept in
    // linkedAdvanceAmounts, read by the Review step's reconciliation ledger
    // - see CurrencyConversionCard below.) The advance is always of this
    // request's own travel type, so its locations map straight onto the
    // fields that type uses. If its country isn't offered under this claim
    // method, the country-sync effect above clears it and the employee picks
    // another. Skipped once a Draft exists: the Draft's trip details are
    // already saved and the wizard can't change them, so overwriting the
    // form would only show details the request doesn't have. NOT skipped
    // during a resubmit edit (isResubmit) - Travel Details is fully
    // reachable/editable there, so changing the linked advance should still
    // auto-fill same as a brand new wizard session would.
    if (advance && (!draftRequestId || isResubmit)) {
      setForm((f) => ({
        ...f,
        department: advance.department,
        country: advance.country,
        originCountry: advance.originCountry || "",
        startCity: advance.startCity || "",
        endCity: advance.endCity || "",
        departureAt: toLocalDatetimeInput(advance.departureAt),
        arrivalAt: toLocalDatetimeInput(advance.arrivalAt),
        flightDetails: advance.flightDetails || ""
      }));
    } else if (!advance && (!draftRequestId || isResubmit)) {
      // Unlinking reverts the same auto-fill - left in place, the advance's
      // trip (country/cities/dates/purpose) silently stayed on the form as
      // if the employee had entered it themselves, with no advance left to
      // explain where it came from (confirmed live: link an advance, switch
      // claim method/travel type away and back, unlink - the old advance's
      // country/cities/dates/purpose were still shown as filled in).
      setForm((f) => ({
        ...f,
        country: "",
        currency: "",
        originCountry: "",
        startCity: "",
        endCity: "",
        departureAt: "",
        arrivalAt: "",
        flightDetails: ""
      }));
    }
    return true;
  };

  // The Unlink button on Travel Details' "Advance linked" badge - same
  // release as unlinking from the Advance step.
  const unlinkFromBadge = async () => {
    setUnlinkingAdvance(true);
    try {
      if (await changeLinkedAdvance(null)) {
        toast.success(
          "Advance unlinked - it's available to link to another request."
        );
      }
    } finally {
      setUnlinkingAdvance(false);
    }
  };

  // Department is auto-filled from the signed-in employee's own Entra
  // profile and never shown/edited on this step (see the Designation field
  // below, which replaces it in the form) - still sent as a real,
  // required field on submit (create_my_request), just no longer something
  // the employee types or sees. Never overwrites a value already set by
  // linking an advance. Skipped entirely while resuming a draft (below),
  // which sets its own department from the saved request.
  //
  // Designation is fetched here purely for DISPLAY (the read-only field
  // below) - it's never part of FormState or the submit body. The backend
  // independently resolves and snapshots the employee's designation itself
  // (requests_api.py's create_my_request reads it straight from
  // UserStore(), storing it as designationAtSubmission), the same
  // profile-not-body pattern already used by create_advance's department
  // fill above - so nothing here needs to be trusted or sent by the client.
  const [designation, setDesignation] = React.useState("");

  // Drives the "Notify Finance" Cc popup (see financeNoticeOpen below): a
  // requester with no reporting manager on file has their request skip
  // straight to PendingFinance at submission, with no separate Manager-
  // approve step to show that popup at - so it's shown right before
  // Submit instead, only for this case. Defaults true (don't show the
  // popup) until the profile fetch below resolves, so a slow/failed fetch
  // fails toward the existing silent-submit behaviour rather than
  // blocking every submit on an extra click.
  const [hasReportingManager, setHasReportingManager] = React.useState(true);
  // Display-only, for the Review & Submit step - who this request's Manager
  // stage will actually route to. Null while hasReportingManager is still
  // resolving, or when there isn't one.
  const [reportingManagerName, setReportingManagerName] = React.useState<
    string | null
  >(null);

  // Every current Finance role holder, for the Finance Approver picker on
  // Travel Details - fetched once, unconditionally (needed whether or not
  // a Draft is being resumed, since resuming skips straight to Bills but
  // the request already has its pick snapshotted from when it was first
  // created; a fresh wizard still needs the live list to populate the
  // dropdown before Travel Details is ever shown).
  const [financeApprovers, setFinanceApprovers] = React.useState<
    FinanceApprover[]
  >([]);
  const [financeApproversLoading, setFinanceApproversLoading] =
    React.useState(true);
  React.useEffect(() => {
    fetchFinanceApprovers()
      .then(setFinanceApprovers)
      .catch(() => setFinanceApprovers([]))
      .finally(() => setFinanceApproversLoading(false));
  }, []);

  React.useEffect(() => {
    if (draftIdToResume) return;
    fetchProfile()
      .then((profile) => {
        if (profile.department) {
          setForm((f) =>
            f.department ? f : { ...f, department: profile.department! }
          );
        }
        if (profile.jobTitle) setDesignation(profile.jobTitle);
        setHasReportingManager(!!profile.hasReportingManager);
        setReportingManagerName(profile.reportingManagerName ?? null);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resuming a Draft (via the "Continue" action on a Draft row in My
  // Requests) - a Draft is created the moment the wizard first reaches the
  // Bills step, for either claim method (see createDraftForBillsStep), so
  // resuming always means: restore every field from the saved request, then
  // jump straight back to Bills rather than replaying Claim Method/Advance?/
  // Travel Details/Currency.
  React.useEffect(() => {
    if (!draftIdToResume) return;
    // This explicit resume is now the authoritative in-progress wizard -
    // drop any OTHER unrelated snapshot saved to sessionStorage from a
    // prior, still-unsubmitted wizard attempt, so it can't leak into a
    // future "start a brand new request" and revive stale state.
    clearWizardSnapshot();
    fetchTravelRequest(draftIdToResume)
      .then((saved) => {
        setForm({
          department: saved.department,
          country: saved.country,
          // GET /travel-requests/:id always stamps a travel type (older
          // Drafts get theirs inferred server-side), so this is only ""
          // for a response that somehow lacks one.
          travelType: saved.travelType ?? "",
          originCountry: saved.originCountry || "",
          startCity: saved.startCity || "",
          endCity: saved.endCity || "",
          departureAt: toLocalDatetimeInput(saved.departureAt),
          arrivalAt: toLocalDatetimeInput(saved.arrivalAt),
          flightDetails: saved.flightDetails || "",
          currency: saved.currency,
          currencyAutoSelected: saved.currencyAutoSelected,
          claimMethod: saved.claimMethod,
          linkedAdvanceId: saved.linkedAdvanceId || null,
          financeApproverId: saved.financeApproverIdAtSubmission || ""
        });
        setFinanceApproverNameAtSubmission(
          saved.financeApproverNameAtSubmission ?? null
        );
        setDraftRequestId(saved.id);
        setExpenses(saved.expenses);
        setDocuments(saved.documents || []);
        setFxOverrides(saved.fxOverrides || []);
        setSettlementTargetByCurrency(saved.settlementTargetByCurrency || {});
        if (saved.linkedAdvanceId) {
          fetchAdvance(saved.linkedAdvanceId)
            .then((a) => setLinkedAdvanceAmounts(a.amounts))
            .catch(() => {});
        }
        if (isResubmit) {
          // Surface the approver's "what needs to change" note - without it
          // the requester lands in an edit wizard with no idea why it came
          // back (see resubmit_my_request / resubmitComment).
          if (saved.resubmitComment) {
            setResubmitNote({
              stage: saved.resubmitRequestedBy === "Finance" ? "Finance" : "Manager",
              comment: saved.resubmitComment
            });
          }
          // Full edit parity, not Bills-only - land on the first real step
          // (Claim Method) so every field is reachable, same as a brand new
          // wizard session.
          setStep(1);
        } else {
          // A Draft only exists once the wizard reached the Bills step (see
          // createDraftForBillsStep), which is at the same position for
          // both claim methods.
          setStep(BILLS_STEP_INDEX);
        }
      })
      .catch(() =>
        setError("Could not load this draft. It may have been discarded.")
      )
      .finally(() => setResuming(false));
    // Resuming skips the Travel Details step entirely (jumps straight to
    // Bills, per this effect's own docstring above) - the profile fetch
    // above that would normally set `designation` for display never runs
    // in that case, so it's fetched here too, purely for the Review step's
    // Designation row.
    fetchProfile()
      .then((profile) => {
        if (profile.jobTitle) setDesignation(profile.jobTitle);
        setHasReportingManager(!!profile.hasReportingManager);
        setReportingManagerName(profile.reportingManagerName ?? null);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftIdToResume]);

  const canPreview = !!(
    form.travelType &&
    form.country &&
    form.departureAt &&
    form.arrivalAt
  );

  // Debounced live preview - fires whenever the fields the calculation
  // depends on change, not on every keystroke.
  React.useEffect(() => {
    if (!canPreview) {
      setPreview(null);
      return;
    }
    const timer = setTimeout(() => {
      setPreviewLoading(true);
      previewAllowance({
        country: form.country,
        travelType: form.travelType as TravelType,
        departureAt: toIso(form.departureAt),
        arrivalAt: toIso(form.arrivalAt),
        claimMethod: form.claimMethod
      })
        .then(setPreview)
        .catch(() => setPreview(null))
        .finally(() => setPreviewLoading(false));
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.country,
    form.travelType,
    form.departureAt,
    form.arrivalAt,
    form.claimMethod,
    canPreview
  ]);

  const validateStep = (): string | null => {
    if (step === claimMethodStepIndex && !form.travelType) {
      return "Select whether this trip is Domestic or International.";
    }
    if (step === advanceStepIndex && advanceToggleOn && !form.linkedAdvanceId) {
      return "Select which advance you received, or turn the toggle off.";
    }
    if (step === travelDetailsStepIndex) {
      if (!form.department || !form.departureAt || !form.arrivalAt) {
        return "Designation, Departure, and Arrival are required.";
      }
      if (!form.flightDetails.trim()) {
        return "Enter the purpose of travel.";
      }
      // Which locations are required depends on the travel type chosen on
      // the first step.
      if (!form.travelType)
        return "Select whether this trip is Domestic or International.";
      const locationError = validateTripLocation(form.travelType, form);
      if (locationError) return locationError;
      if (new Date(form.arrivalAt) <= new Date(form.departureAt)) {
        return "Arrival must be after departure.";
      }
      if (isReimbursement) {
        // Compared by calendar date, not exact time - a trip arriving
        // "today" is a completed-enough trip to claim regardless of what
        // time of day it currently is (reported bug: a same-day arrival
        // typed before that exact clock time wrongly failed this check).
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const departureDay = new Date(form.departureAt);
        departureDay.setHours(0, 0, 0, 0);
        const arrivalDay = new Date(form.arrivalAt);
        arrivalDay.setHours(0, 0, 0, 0);
        if (departureDay > today || arrivalDay > today) {
          return "Reimbursement claims a completed trip - Departure and Arrival can't be in the future.";
        }
      }
      if (!form.currency) {
        return "Select a currency.";
      }
      // Only enforced when at least one Finance Approver is actually
      // configured - an org with none yet (an admin-config gap) shouldn't
      // block every employee from submitting anything; the backend
      // already tolerates a null financeApproverId the same way it
      // tolerates no manager on file.
      if (
        !isResubmit &&
        !financeApproversLoading &&
        financeApprovers.length > 0 &&
        !form.financeApproverId
      ) {
        return "Select a Finance Approver.";
      }
    }
    // Fixed Allowance bills are optional (tracking only).
    if (isReimbursement && step === billsStepIndex && expenses.length === 0) {
      return "Add at least one bill before continuing.";
    }
    return null;
  };

  const createDraftForBillsStep = async () => {
    setCreatingDraft(true);
    setError(null);
    try {
      const { request: created } = await createMyRequest({
        department: form.department,
        ...tripPayload(form),
        departureAt: toIso(form.departureAt),
        arrivalAt: toIso(form.arrivalAt),
        flightDetails: form.flightDetails || undefined,
        currency: form.currency,
        currencyAutoSelected: form.currencyAutoSelected,
        claimMethod: form.claimMethod,
        linkedAdvanceId: form.linkedAdvanceId || undefined,
        financeApproverId: form.financeApproverId || undefined,
        saveAsDraft: true
      });
      setDraftRequestId(created.id);
      setExpenses(created.expenses);
      setDocuments(created.documents || []);
      setFxOverrides(created.fxOverrides || []);
      setSettlementTargetByCurrency(created.settlementTargetByCurrency || {});
      return true;
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not start this request - check your connection and try again."
      );
      return false;
    } finally {
      setCreatingDraft(false);
    }
  };

  const next = async () => {
    // Toasted (top-right, red - see components/ui/sonner.tsx), not the
    // inline message this used to set - that rendered inside the step's
    // own scrollable content (see the step !== reviewStepIndex render this
    // replaces), so clicking Next from a field near the top of a long step
    // showed an error invisible until scrolling all the way down to find
    // it. Submit-time failures on the Review step (setError elsewhere in
    // this file) are untouched - those still show inline there, and are
    // also what FinanceNoticeDialog's own `error` prop displays.
    if (step === billsStepIndex && unaddedBills > 0) {
      toast.error(
        unaddedBills === 1
          ? "You have a bill that hasn't been added yet. Click Add bill to save it, or remove it with × to skip it."
          : `You have ${unaddedBills} bills that haven't been added yet. Click Add all ${unaddedBills} bills to save them, or remove them with × to skip them.`
      );
      return;
    }
    if (step === billsStepIndex && editingExpense) {
      toast.error(
        "You're editing a bill. Save your changes, or close the edit form, before continuing."
      );
      return;
    }
    if (step === billsStepIndex && documentsBusy) {
      toast.error(
        "A document is still uploading. Wait for it to finish before continuing."
      );
      return;
    }
    const err = validateStep();
    if (err) {
      toast.error(err);
      return;
    }
    setError(null);

    // Entering the bills step for the first time (either claim method) -
    // create the Draft request now so uploads have somewhere real to go.
    if (step === travelDetailsStepIndex && !draftRequestId) {
      const ok = await createDraftForBillsStep();
      if (!ok) return;
    }
    // Editing for resubmission, OR a Draft that already exists (its claim
    // method/trip may have changed since it was first created - the
    // employee went back past the Bills step to Step 1 and picked a
    // different claim method, country, etc.): save the edits now, so the
    // Bills step (allowed currencies, over-policy flags), the linked
    // advance, and ultimately the final submit all work against what's
    // actually on screen, not whatever the Draft happened to be created
    // with. Without this, going back to change claim method after the
    // Draft already existed updated only the wizard's own local state - the
    // server-side Draft kept its original claimMethod/trip forever, and
    // THAT is what finalizeMyRequest submitted (a real, confirmed bug:
    // Fixed Allowance + Domestic created the Draft, switching to
    // Reimbursement afterward looked correct throughout the rest of the
    // wizard, but submitted as the original Fixed Allowance + Domestic).
    if (step === travelDetailsStepIndex && draftRequestId) {
      const ok = await saveEditsForResubmit(draftRequestId);
      if (!ok) return;
    }
    setStep((s) => Math.min(s + 1, steps.length));
  };

  // Everything the employee can change while fixing a request sent back to
  // them. The server re-prices the trip from this (estimate, currency,
  // policy snapshot, bill flags) - see requests_api.py's
  // _apply_resubmit_edit. linkedAdvanceId is sent even when null, which
  // releases the advance the request held.
  const resubmitEditPayload = () => ({
    department: form.department,
    ...tripPayload(form),
    departureAt: toIso(form.departureAt),
    arrivalAt: toIso(form.arrivalAt),
    flightDetails: form.flightDetails || undefined,
    currencyAutoSelected: form.currencyAutoSelected,
    claimMethod: form.claimMethod,
    linkedAdvanceId: form.linkedAdvanceId
  });

  const saveEditsForResubmit = async (requestId: string) => {
    setSavingResubmitEdits(true);
    try {
      const updated = await saveResubmitEdits(requestId, resubmitEditPayload());
      setExpenses(updated.expenses);
      setFxOverrides(updated.fxOverrides || []);
      setSettlementTargetByCurrency(updated.settlementTargetByCurrency || {});
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.error(
          err.message ||
            "This request has already changed (maybe from another tab) - taking you to My Requests to see its current status."
        );
        clearWizardSnapshot();
        navigate("/my-requests");
        return false;
      }
      toast.error(
        err instanceof ApiError
          ? err.message
          : "Couldn't save your changes - check your connection and try again."
      );
      return false;
    } finally {
      setSavingResubmitEdits(false);
    }
  };
  // Bills-step state (unadded cards, an open inline edit) lives in
  // ExpensesStep's own component state (pending/editing), not in this
  // wizard's lifted form state - so unlike every other step, leaving Bills
  // doesn't just hide that work, it actually destroys it on unmount. next()
  // already blocks outright rather than risk that silently; leaving
  // backward instead confirms, since going back (or away entirely) to
  // re-check something is a legitimate thing to want mid-review and the
  // employee should get to choose, not be flatly stopped the way a forward
  // Next is. This same risk condition gates TWO independent exit paths:
  // 1. The in-wizard Back button, which only moves this component's own
  //    `step` state - no real browser navigation happens, so useBlocker
  //    below can't see it at all.
  // 2. Any ACTUAL navigation away from this route while the condition holds
  //    - browser back/forward/swipe-back, a sidebar/header Link click, or
  //    typed-URL/reload (that last one via beforeunload, since useBlocker
  //    itself only covers in-SPA client-side navigation, never a full
  //    document load - see react-router's own useBlocker docs). This is
  //    the reason App.tsx was migrated to a data router (createBrowserRouter
  //    /RouterProvider) - useBlocker requires one and does not work under
  //    a plain BrowserRouter.
  // Both paths share ONE dialog and message; only what "confirm" actually
  // does differs (decrement step vs. let the blocked navigation proceed).
  const hasUnsavedBillWork =
    step === billsStepIndex && (unaddedBills > 0 || editingExpense);

  const blocker = useBlocker(hasUnsavedBillWork);

  React.useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!hasUnsavedBillWork) return;
      // No custom text - every modern browser shows its own generic
      // "Leave site?" wording regardless of what's set here (a long-
      // standing restriction so a page can't phish with a fake dialog);
      // setting returnValue at all is what actually triggers the prompt.
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedBillWork]);

  const goBack = async () => {
    setError(null);
    // Leaving Travel Details BACKWARD (to Advance?) with a Draft already on
    // the server needs the same sync next() already does when leaving it
    // FORWARD (to Bills) - without this, editing the trip (country/currency/
    // claim method) then clicking Back instead of Next left the edit only in
    // the wizard's own local state: the Allowance Preview and every other
    // on-screen figure updated immediately, but the server-side Draft kept
    // whatever currency/trip it was created or last saved with. Confirmed
    // live: changed the trip's currency to USD on Travel Details, clicked
    // Back to Advance? to link a USD advance - the preview correctly showed
    // USD, but linking failed ("That advance is in USD... but this request
    // is in ALL") because the Draft itself was still on the OLD currency.
    if (step === travelDetailsStepIndex && draftRequestId) {
      const ok = await saveEditsForResubmit(draftRequestId);
      if (!ok) return;
    }
    setStep((s) => Math.max(s - 1, 1));
  };
  // True for either trigger - the in-wizard Back button (manualBackPending)
  // or useBlocker catching a real navigation attempt (blocker.state).
  const [manualBackPending, setManualBackPending] = React.useState(false);
  const confirmBackOpen = manualBackPending || blocker.state === "blocked";
  const back = () => {
    if (hasUnsavedBillWork) {
      setManualBackPending(true);
      return;
    }
    void goBack();
  };
  const confirmDiscardAndLeave = () => {
    if (manualBackPending) {
      setManualBackPending(false);
      // This path only fires from the Bills step's unsaved-work prompt
      // (hasUnsavedBillWork), never from Travel Details, so goBack's own
      // save-before-leaving branch never applies here - void'd the same way
      // back() above does, just to satisfy the no-floating-promises rule.
      void goBack();
    }
    if (blocker.state === "blocked") blocker.proceed();
  };
  const cancelLeaving = () => {
    setManualBackPending(false);
    if (blocker.state === "blocked") blocker.reset();
  };

  // Shown instead of submitting directly when the requester has no
  // reporting manager (see hasReportingManager above) - their request has
  // no separate Manager-approve step to show FinanceNoticeDialog at, so
  // it's shown here, before the request is actually created, mirroring
  // the same "nothing happens until Send" design MyApprovals.tsx's popup
  // uses for a Manager approving an existing one.
  const [financeNoticeOpen, setFinanceNoticeOpen] = React.useState(false);
  const [financeCc, setFinanceCc] = React.useState<string[]>([]);

  const submit = async (ccEmails?: string[]) => {
    setSubmitting(true);
    setError(null);
    try {
      if (isResubmit) {
        // UNLIKE finalizeMyRequest/createMyRequest, this does not create a
        // new Manager approval - it routes back to whichever stage sent it
        // back, reusing that stage's existing, untouched approval record
        // (see requests_api.py's resubmit_my_request /
        // ApprovalStore.complete_resubmission). draftRequestId is actually
        // this ResubmitRequested request's own id here - the resume effect
        // above loaded it the same way it loads a real Draft's id.
        if (!draftRequestId) return;
        // The same edits the Travel Details step already saved, applied once
        // more (the employee may have gone back and changed something) -
        // claim method and linked advance included, both validated against
        // the edited trip server-side.
        await resubmitMyRequest(draftRequestId, resubmitEditPayload());
        toast.success("Request resubmitted");
        clearWizardSnapshot();
        navigate("/my-requests");
        return;
      }
      // Normally a Draft exists by now (created on entering the Bills step,
      // for either claim method) and is finalized. The one-shot create is
      // only a fallback for a session that somehow reached Review without
      // one.
      const result = draftRequestId
        ? await finalizeMyRequest(draftRequestId, ccEmails, form.financeApproverId || undefined)
        : await createMyRequest({
              department: form.department,
              ...tripPayload(form),
              departureAt: toIso(form.departureAt),
              arrivalAt: toIso(form.arrivalAt),
              flightDetails: form.flightDetails || undefined,
              currency: form.currency,
              currencyAutoSelected: form.currencyAutoSelected,
              claimMethod: form.claimMethod,
              linkedAdvanceId: form.linkedAdvanceId || undefined,
              financeApproverId: form.financeApproverId || undefined,
              ccEmails
            });
      // No reporting manager on file - the Manager-approval stage was
      // skipped entirely and this went straight to PendingFinance (see
      // requests_api.py's _route_for_manager_approval). Surfaced here since
      // it's otherwise invisible to the requester: the submit just silently
      // succeeds and they'd have no way to tell their request skipped a
      // whole approval stage.
      if (result.skippedToFinance) {
        toast.success(
          "Submitted - you have no reporting manager on file, so this went straight to your Finance Approver."
        );
      } else {
        toast.success("Request submitted");
      }
      setFinanceNoticeOpen(false);
      clearWizardSnapshot();
      navigate("/my-requests");
    } catch (err) {
      // This request/Draft moved on elsewhere (e.g. the same
      // ResubmitRequested request being resubmitted from a second tab, or
      // decided/discarded there) - the server 409s instead of silently
      // double-submitting. There's nothing left to finish in this wizard
      // against a document that's already moved on, so toast the server's
      // own explanation and leave for My Requests, which shows what
      // actually happened, instead of leaving the form sitting here.
      if (err instanceof ApiError && err.status === 409) {
        toast.error(
          err.message ||
            "This request has already changed (maybe from another tab, or your approver acted on it) - taking you to My Requests to see its current status."
        );
        clearWizardSnapshot();
        navigate("/my-requests");
        return;
      }
      setError(
        err instanceof ApiError
          ? err.message
          : `Could not ${isResubmit ? "resubmit" : "submit"} this request - check your connection and try again.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  // The Submit button's click target — shows a last confirmation before
  // actually doing anything (confirmSubmit below is where the real branch
  // happens).
  const handleSubmitClick = () => setConfirmSubmitOpen(true);

  // The confirmation's own confirm action — opens the Notify Finance Cc
  // popup first for a manager-less requester (that popup's own Send is
  // what actually calls submit(), see below), otherwise submits straight
  // away. Same branch handleSubmitClick used to run directly, before this
  // confirmation step existed. Resubmitting (isResubmit) skips the popup
  // entirely regardless of hasReportingManager - there's no "notify" moment
  // on a resubmit, same as every other Resubmit action in this app; it
  // routes back to whichever stage already sent it back, reusing that
  // stage's existing approval record.
  const confirmSubmit = () => {
    setConfirmSubmitOpen(false);
    if (!isResubmit && !hasReportingManager) {
      setFinanceCc([]);
      setFinanceNoticeOpen(true);
      return;
    }
    submit();
  };

  if (resuming) {
    return (
      <PageContainer>
        <PageHeader
          title="Create Travel Reimbursement Request"
          description="Loading your draft…"
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Create Travel Reimbursement Request"
        description="Fill in the details below to submit your travel request for approval."
      />

      <WizardStepper steps={steps} currentStep={step} />

      {resubmitNote && (
        <div className="flex items-start gap-3 rounded-md border border-[hsl(var(--quadra-orange))] bg-[hsl(var(--quadra-orange)/0.1)] p-4 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
          <div className="space-y-0.5">
            <p className="font-medium text-amber-700 dark:text-amber-400">
              Changes requested by{" "}
              {resubmitNote.stage === "Finance"
                ? "Finance"
                : "your reporting manager"}
            </p>
            <p className="whitespace-pre-wrap text-foreground">
              {resubmitNote.comment}
            </p>
          </div>
        </div>
      )}

      <div
        className={cn(
          "grid gap-6",
          // Allowance Preview is hidden on Review & Submit (see below) - the
          // card takes the full width there instead of leaving its column's
          // 20rem empty.
          step === reviewStepIndex ? "lg:grid-cols-1" : "lg:grid-cols-[1fr_20rem]"
        )}
      >
        {/* On desktop the card is capped to the viewport and its step
            content scrolls inside a ScrollArea, keeping the action bar pinned
            at the card's foot. On phones/tablet the card keeps its natural
            height and the page scrolls as before (the sticky action bar below
            handles that), so the cap and inner scroll are lg-only. */}
        <Card className="flex min-w-0 flex-col lg:max-h-[calc(100vh-13rem)]">
          <CardContent className="flex min-h-0 flex-1 flex-col gap-4 p-4 sm:p-6">
            {/* Radix renders the ScrollArea viewport's child as
                `display:table; min-width:100%`, which shrink-wraps to its
                content and GROWS past the parent instead of clipping - a long
                unbreakable string (e.g. an uploaded document's filename on the
                Bills step) then pushed the whole card wider than a phone
                viewport. Forcing that child to a plain, width-bounded block
                lets its contents wrap/truncate normally. Scoped here (not in
                the shared ScrollArea) because the horizontal ScrollBars in
                DataTable/AuditLogs deliberately rely on that overflow. */}
            <ScrollArea className="-mx-4 min-h-0 flex-1 sm:-mx-6 [&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]>div]:!min-w-0">
              {/* py-1: the ScrollArea viewport clips at its edges, so without
                  vertical room the focus ring of the first/last field (e.g.
                  Travel details, the last field on its step) was cut off. */}
              <div className="space-y-4 px-4 py-1 sm:px-6">
                {step === claimMethodStepIndex && (
                  <>
                    <Label>How will you claim this trip?</Label>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {(
                        ["fixed_allowance", "reimbursement"] as ClaimMethod[]
                      ).map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => chooseClaimMethod(m)}
                          className={`rounded-md border p-4 text-left text-sm transition-colors sm:p-3 ${
                            form.claimMethod === m
                              ? "border-primary bg-primary/5 ring-1 ring-primary"
                              : "border-border hover:bg-muted/50"
                          }`}
                        >
                          <p className="font-medium">{CLAIM_METHOD_LABEL[m]}</p>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {m === "fixed_allowance"
                              ? "Receive a predefined amount - no bills required (tax may apply)."
                              : "Claim actual expenses with bills, within policy limits."}
                          </p>
                        </button>
                      ))}
                    </div>
                    <TravelTypePicker
                      value={form.travelType}
                      onChange={chooseTravelType}
                    />
                    {form.travelType && (
                      <p className="text-xs text-muted-foreground">
                        The {TRAVEL_TYPE_LABEL[form.travelType]}{" "}
                        {CLAIM_METHOD_LABEL[form.claimMethod]} policy will apply
                        to this request.
                      </p>
                    )}
                  </>
                )}

                {step === advanceStepIndex && form.travelType && (
                  <LinkAdvanceField
                    travelType={form.travelType}
                    claimMethod={form.claimMethod}
                    linkedAdvanceId={form.linkedAdvanceId}
                    onChange={changeLinkedAdvance}
                    fillsTravelDetails={!draftRequestId}
                    enabled={advanceToggleOn}
                    onToggleChange={setAdvanceToggleOn}
                  />
                )}

                {step === travelDetailsStepIndex && (
                  <>
                    <div className="space-y-1.5">
                      <Label>Designation</Label>
                      <Input value={designation || "-"} disabled readOnly />
                    </div>
                    {form.travelType ? (
                      <>
                        <div className="space-y-1.5">
                          <Label>Claim type</Label>
                          <Input
                            value={`${TRAVEL_TYPE_LABEL[form.travelType]} - ${CLAIM_METHOD_LABEL[form.claimMethod]}`}
                            disabled
                            readOnly
                          />
                        </div>
                        {form.linkedAdvanceId && (
                          <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
                            <CircleCheck className="h-4 w-4 shrink-0 text-primary" />
                            <p className="min-w-0 flex-1">
                              Advance linked
                              {linkedAdvanceAmounts !== null &&
                                ` - ${formatAdvanceAmounts(linkedAdvanceAmounts)} received`}
                              .
                              {!draftRequestId &&
                                " Travel details below were filled in from it."}
                            </p>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => void unlinkFromBadge()}
                              disabled={unlinkingAdvance}
                            >
                              {unlinkingAdvance && (
                                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                              )}
                              Unlink
                            </Button>
                          </div>
                        )}
                        <TripLocationFields
                          travelType={form.travelType}
                          value={{
                            country: form.country,
                            originCountry: form.originCountry,
                            startCity: form.startCity,
                            endCity: form.endCity
                          }}
                          onChange={(loc) => {
                            const currency =
                              allowedCountries.find(
                                (c) => c.country === loc.country
                              )?.currency || "";
                            setForm((f) => ({ ...f, ...loc, currency }));
                          }}
                          allowedCountries={allowedCountries}
                          loading={allowedCountriesLoading}
                          emptyHint={`No ${TRAVEL_TYPE_LABEL[form.travelType]} countries are configured for ${CLAIM_METHOD_LABEL[form.claimMethod]} yet. Please contact your admin for configuration.`}
                          idPrefix="request"
                        />
                      </>
                    ) : (
                      <p className="text-sm text-destructive">
                        Go back and choose whether this trip is Domestic or
                        International.
                      </p>
                    )}
                    {isResubmit ? (
                      // A resubmitted request returns to the same approvers
                      // it already had (see resubmit_my_request), so the
                      // pick can't change here.
                      <div className="space-y-1.5">
                        <Label>Finance Approver</Label>
                        <Input
                          value={
                            financeApprovers.find(
                              (a) => a.id === form.financeApproverId
                            )?.displayName ||
                            financeApproverNameAtSubmission ||
                            "-"
                          }
                          disabled
                          readOnly
                        />
                        <p className="text-xs text-muted-foreground">
                          Your request returns to the same approvers when you
                          resubmit, so this can't be changed.
                        </p>
                      </div>
                    ) : (
                    <div className="space-y-1.5">
                      <Label>Finance Approver</Label>
                      <Combobox
                        value={form.financeApproverId}
                        onChange={(v) => set("financeApproverId", v)}
                        options={financeApprovers.map((a) => ({
                          value: a.id,
                          label: a.displayName || a.email || a.id,
                          sublabel: a.email || undefined
                        }))}
                        placeholder={
                          financeApproversLoading
                            ? "Loading…"
                            : financeApprovers.length === 0
                              ? "None configured"
                              : "Select a Finance Approver"
                        }
                        searchPlaceholder="Search…"
                        disabled={
                          financeApproversLoading ||
                          financeApprovers.length === 0
                        }
                      />
                      {!financeApproversLoading &&
                        financeApprovers.length === 0 && (
                          <p className="text-xs text-amber-600 dark:text-amber-400">
                            No Finance Approver is configured yet. Please
                            contact your admin.
                          </p>
                        )}
                    </div>
                    )}
                    <div className="grid items-end gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label>Departure date &amp; time</Label>
                        {/* Reimbursement claims a trip that already happened, so
                       neither date can be in the future (Fixed Allowance has
                       no such restriction - it's a forward-looking entitlement,
                       not a claim against completed travel). */}
                        <DateTimePicker
                          value={form.departureAt}
                          onChange={(v) => set("departureAt", v)}
                          maxDate={isReimbursement ? new Date() : undefined}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Arrival date &amp; time</Label>
                        <DateTimePicker
                          value={form.arrivalAt}
                          onChange={(v) => set("arrivalAt", v)}
                          maxDate={isReimbursement ? new Date() : undefined}
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="flight">Purpose of travel</Label>
                      <Textarea
                        id="flight"
                        value={form.flightDetails}
                        onChange={(e) => set("flightDetails", e.target.value)}
                        placeholder="e.g. Client meeting, site visit"
                      />
                    </div>
                  </>
                )}

                {step === billsStepIndex && (
                  <div className="space-y-4">
                    {creatingDraft && (
                      <p className="text-sm text-muted-foreground">
                        Starting your request…
                      </p>
                    )}
                    {!isReimbursement && (
                      <p className="rounded-md border bg-muted/50 p-3 text-sm text-muted-foreground">
                        Optional. Record what you actually spent on this trip
                        for tracking - it doesn't change your fixed
                        allowance. You can skip this step.
                      </p>
                    )}
                    {draftRequestId && (
                      // Two tabs: the claim bills (ExpensesStep, unchanged)
                      // and plain supporting attachments (DocumentsStep). Only
                      // the bills feed the estimate/policy; documents are
                      // reference-only. The count badges mirror the reference
                      // design.
                      <Tabs defaultValue="bills" className="w-full min-w-0">
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger
                            value="bills"
                            className="min-w-0 gap-1.5"
                          >
                            <Receipt className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">
                              {isReimbursement
                                ? "Bills & Receipts"
                                : "Expenses"}
                            </span>
                            {expenses.length > 0 && (
                              <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground">
                                {expenses.length}
                              </span>
                            )}
                          </TabsTrigger>
                          <TabsTrigger
                            value="documents"
                            className="min-w-0 gap-1.5"
                          >
                            <FileText className="h-3.5 w-3.5 shrink-0" />
                            {/* "Additional Documents" overflows a narrow
                                phone tab - shortened to "Documents" below
                                sm, full label from sm up. */}
                            <span className="truncate">
                              <span className="sm:hidden">Documents</span>
                              <span className="hidden sm:inline">
                                Additional Documents
                              </span>
                            </span>
                            {documents.length > 0 && (
                              <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-muted-foreground/20 px-1.5 text-xs font-medium text-foreground">
                                {documents.length}
                              </span>
                            )}
                          </TabsTrigger>
                        </TabsList>
                        {/* forceMount on BOTH, kept CSS-hidden (not
                            unmounted) while inactive via Radix's own
                            data-state attribute - Tabs/TabsContent's default
                            behavior unmounts the inactive panel entirely
                            (Presence present={isSelected}), which would
                            silently destroy ExpensesStep's own component
                            state (unadded bill cards, an open inline edit -
                            see hasUnsavedBillWork above) on every switch to
                            Documents and back, the same loss the exit-guard
                            above protects against on navigation/Back - a tab
                            switch isn't navigation at all, so nothing else
                            here would catch it. Keeping both mounted means
                            there's nothing TO lose, so no confirmation is
                            needed for this one - simpler and lower-friction
                            than gating the switch with another dialog. */}
                        <TabsContent
                          value="bills"
                          forceMount
                          className="mt-4 min-w-0 data-[state=inactive]:hidden"
                        >
                          <ExpensesStep
                            travelRequestId={draftRequestId}
                            expenses={expenses}
                            onChanged={setExpenses}
                            defaultCurrency={form.currency}
                            claimMethod={form.claimMethod}
                            onBusyChange={setExpensesBusy}
                            onPendingChange={setUnaddedBills}
                            onEditingChange={setEditingExpense}
                          />
                        </TabsContent>
                        <TabsContent
                          value="documents"
                          forceMount
                          className="mt-4 min-w-0 data-[state=inactive]:hidden"
                        >
                          <DocumentsStep
                            travelRequestId={draftRequestId}
                            documents={documents}
                            onChanged={setDocuments}
                            onBusyChange={setDocumentsBusy}
                          />
                        </TabsContent>
                      </Tabs>
                    )}
                  </div>
                )}

                {step === reviewStepIndex && (
                  <div className="space-y-3 text-sm">
                    <SummaryRow
                      label="Designation"
                      value={designation || "-"}
                    />
                    {hasReportingManager && (
                      <SummaryRow
                        label="Reporting Manager"
                        value={reportingManagerName || "-"}
                      />
                    )}
                    <SummaryRow
                      label="Trip type"
                      value={
                        form.travelType
                          ? TRAVEL_TYPE_LABEL[form.travelType]
                          : "-"
                      }
                    />
                    {form.travelType === "international" ? (
                      <>
                        <SummaryRow
                          label="Starting country"
                          value={form.originCountry || "-"}
                        />
                        <SummaryRow
                          label="Final destination country"
                          value={form.country || "-"}
                        />
                      </>
                    ) : (
                      <>
                        <SummaryRow
                          label="Country"
                          value={form.country || "-"}
                        />
                        {form.travelType === "domestic" && (
                          <>
                            <SummaryRow
                              label="Start city"
                              value={form.startCity || "-"}
                            />
                            <SummaryRow
                              label="End city"
                              value={form.endCity || "-"}
                            />
                          </>
                        )}
                      </>
                    )}
                    <SummaryRow
                      label="Departure"
                      value={
                        form.departureAt
                          ? formatDateTime(form.departureAt)
                          : "-"
                      }
                    />
                    <SummaryRow
                      label="Arrival"
                      value={
                        form.arrivalAt
                          ? formatDateTime(form.arrivalAt)
                          : "-"
                      }
                    />
                    {form.flightDetails && (
                      <SummaryRow
                        label="Purpose of travel"
                        value={form.flightDetails}
                      />
                    )}
                    <SummaryRow label="Currency" value={form.currency} />
                    <SummaryRow
                      label="Claim method"
                      value={CLAIM_METHOD_LABEL[form.claimMethod]}
                    />
                    {form.financeApproverId && (
                      <SummaryRow
                        label="Finance Approver"
                        value={
                          financeApprovers.find(
                            (a) => a.id === form.financeApproverId
                          )?.displayName || form.financeApproverId
                        }
                      />
                    )}

                    {!isReimbursement && preview?.claimMethod === "fixed_allowance" && (
                      <SummaryRow
                        label="Estimated allowance"
                        value={`${preview.currency} ${preview.estimatedAllowanceAmount.toLocaleString()}`}
                      />
                    )}

                    {/* Advance received / Also received (reconciled by
                        Finance) used to show here as plain rows - removed in
                        favour of the Multi-currency reconciliation ledger
                        below, which already shows every advance line (its
                        own "Advance issued" column) alongside the bills. Its
                        own "Net payable" tile (CurrencyConversionCard, via
                        its estimatedAllowanceAmount prop below) replaced a
                        separate SummaryRow here too - that row only ever
                        netted the one advance line already matching
                        form.currency, so it went stale/wrong the moment the
                        reconciliation table above it converted another
                        currency's line into form.currency instead (confirmed
                        live: converting a EUR advance line to SGD moved the
                        table's own total but left the separate row still
                        showing the old, narrower SGD-only net). */}

                    {/* Per-currency actual-spend figures live in the
                        reconciliation ledger below (CurrencyConversionCard) -
                        just the count here, not a second copy of the same
                        breakdown. */}
                    {!isReimbursement && expenses.length > 0 && (
                      <SummaryRow
                        label="Expenses recorded"
                        value={
                          <span className="inline-flex items-center gap-1.5">
                            {expenses.length}
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-primary"
                              onClick={() => setViewingBills(true)}
                              title="View uploaded bills"
                              aria-label="View uploaded bills"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        }
                      />
                    )}

                    {isReimbursement && (
                      <SummaryRow
                        label="Bills uploaded"
                        value={
                          expenses.length > 0 ? (
                            <span className="inline-flex items-center gap-1.5">
                              {expenses.length}
                              <button
                                type="button"
                                className="text-muted-foreground hover:text-primary"
                                onClick={() => setViewingBills(true)}
                                title="View uploaded bills"
                                aria-label="View uploaded bills"
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </button>
                            </span>
                          ) : (
                            "0"
                          )
                        }
                      />
                    )}

                    {/* Fixed Allowance's own rows above cover the allowance
                        itself (the actual claim); this reconciles the
                        optional tracking bills against the advance instead,
                        same mechanism as Reimbursement - shown alongside,
                        not instead of, the allowance figures. Renders
                        whenever there's a bill OR an advance, OR it's Fixed
                        Allowance at all (even with neither) - that last case
                        still has a real job: the settlement-currency picker
                        and the Net payable tile, which CurrencyConversionCard
                        itself now renders even with nothing to reconcile
                        (see its own hasAnyCurrency handling) - gating the
                        whole component out here before it even mounts
                        undid that (confirmed live: a Fixed Allowance request
                        with 0 bills and 0 advance showed no card at all,
                        despite CurrencyConversionCard being able to render
                        one). Previously gated on bills alone, which ALSO hid
                        the whole ledger for a Fixed Allowance request that
                        received a multi-currency advance but hadn't uploaded
                        any tracking bills yet. */}
                    {(expenses.length > 0 ||
                      (linkedAdvanceAmounts?.length ?? 0) > 0 ||
                      !isReimbursement) && (
                      <CurrencyConversionCard
                        travelRequestId={draftRequestId ?? undefined}
                        expenses={expenses}
                        advanceAmounts={linkedAdvanceAmounts ?? undefined}
                        fxOverrides={fxOverrides}
                        settlementTargetByCurrency={settlementTargetByCurrency}
                        defaultTargetCurrency={form.currency}
                        departureDate={form.departureAt}
                        claimMethod={form.claimMethod}
                        // Fixed Allowance only - lets the card's own "Net
                        // payable" tile net the allowance against the FULL
                        // advance as currently converted in the table above
                        // (every row's own settlement currency/override),
                        // not just whichever one line happens to already
                        // match form.currency - see CurrencyConversionCard's
                        // own doc comment on why a separate, narrower
                        // SummaryRow here would go stale the moment the
                        // employee repoints a row's settlement currency.
                        estimatedAllowanceAmount={
                          !isReimbursement && preview?.claimMethod === "fixed_allowance"
                            ? preview.estimatedAllowanceAmount
                            : undefined
                        }
                        onOverridesChanged={(updated) => {
                          setFxOverrides(updated.fxOverrides || []);
                          setSettlementTargetByCurrency(updated.settlementTargetByCurrency || {});
                        }}
                      />
                    )}

                    {error && (
                      <p className="text-sm text-destructive">{error}</p>
                    )}
                  </div>
                )}

              </div>
            </ScrollArea>

            {/* Action bar pinned at the card's foot, outside the scroll
               region so it stays visible while the step content scrolls. On
               phones (the bottom tab bar is hidden on this route - see
               AppShell) it's a sticky bar with full-width touch-sized
               buttons; from sm up it's the plain inline button row. */}
            <div className="sticky bottom-0 z-10 -mx-4 -mb-4 flex shrink-0 justify-between gap-3 rounded-b-lg border-t bg-card/95 px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur max-sm:[&>button]:h-11 max-sm:[&>button]:flex-1 sm:static sm:mx-0 sm:mb-0 sm:rounded-none sm:border-0 sm:bg-transparent sm:p-0 sm:pt-2 sm:backdrop-blur-none">
              <Button
                variant="outline"
                onClick={step === 1 ? () => navigate("/my-requests") : back}
              >
                {step === 1 ? "Cancel" : "Back"}
              </Button>
              {step < steps.length ? (
                <Button
                  onClick={next}
                  disabled={
                    creatingDraft ||
                    savingResubmitEdits ||
                    (step === billsStepIndex && expensesBusy) ||
                    (step === billsStepIndex && documentsBusy) ||
                    // A bill card - manually added or from an upload, either
                    // claim method - sitting filled-in but not yet committed
                    // with "Add bill"/"Add all bills" must block Next
                    // outright, not just toast on a click that's let through:
                    // the toast in next() only fires AFTER a click, which
                    // still requires the employee to notice the warning
                    // disappear and try again - disabling the button here
                    // means there's nothing to click until the card is
                    // actually added or removed.
                    (step === billsStepIndex && unaddedBills > 0) ||
                    // Same reasoning as unaddedBills above, for a bill's
                    // inline edit form left open instead of a new card left
                    // unadded - either way, Next must have nothing to click
                    // until the in-progress change is actually resolved
                    // (saved or cancelled), not just a toast after the fact.
                    (step === billsStepIndex && editingExpense) ||
                    (isReimbursement &&
                      step === billsStepIndex &&
                      expenses.length === 0)
                  }
                >
                  Next <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                </Button>
              ) : (
                <Button onClick={handleSubmitClick} disabled={submitting}>
                  {submitting ? "Submitting…" : "Submit request"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {step !== reviewStepIndex && (
          <div className="space-y-4">
            <AllowancePreviewCard
              preview={preview}
              loading={previewLoading}
              currency={form.currency}
              // Only once the Bills step has actually been reached - earlier
              // steps (Claim Method, Advance, Travel Details) have nothing to
              // do with bills at all, and `expenses` is still `[]` there
              // regardless of claim method, which the card can't tell apart
              // from "Reimbursement, zero bills so far" - omitting the prop
              // here is what lets it fall back to the plain per-day reference
              // view instead of showing "Total bills claimed / 0" too early.
              expenses={step >= billsStepIndex ? expenses : undefined}
            />
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmBackOpen}
        onOpenChange={(open) => {
          if (!open) cancelLeaving();
        }}
        title="Discard unsaved bill changes?"
        description={
          editingExpense
            ? "You're editing a bill. Leaving now discards that edit - it was never saved."
            : unaddedBills === 1
              ? "You have a bill that hasn't been added yet. Leaving now discards it."
              : `You have ${unaddedBills} bills that haven't been added yet. Leaving now discards all of them.`
        }
        confirmLabel="Discard and leave"
        destructive
        onConfirm={confirmDiscardAndLeave}
      />

      <ConfirmDialog
        open={confirmSubmitOpen}
        onOpenChange={setConfirmSubmitOpen}
        title="Submit this travel request?"
        description="Review the details above before continuing. Once submitted, it's routed for approval and can't be edited."
        confirmLabel="Submit request"
        pending={submitting}
        onConfirm={confirmSubmit}
      />

      {financeNoticeOpen && (
        <FinanceNoticeDialog
          open
          onClose={() => {
            setFinanceNoticeOpen(false);
            setFinanceCc([]);
          }}
          toName={
            financeApprovers.find((a) => a.id === form.financeApproverId)
              ?.displayName ?? null
          }
          toEmail={
            financeApprovers.find((a) => a.id === form.financeApproverId)
              ?.email ?? null
          }
          cc={financeCc}
          onCcChange={setFinanceCc}
          onSend={() => submit(financeCc)}
          sending={submitting}
          error={error}
        />
      )}

      {/* View-only - no onEditExpense, so BillsSummaryCard shows no edit
          pencil; it never offers add/delete regardless (see its own doc
          comment). Correcting a bill still means going back to the Bills
          step. */}
      <Dialog open={viewingBills} onOpenChange={setViewingBills}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Uploaded bills</DialogTitle>
          </DialogHeader>
          {draftRequestId && (
            <BillsSummaryCard
              requestId={draftRequestId}
              expenses={expenses}
              currency={form.currency}
              estimatedAllowanceAmount={preview?.estimatedAllowanceAmount ?? 0}
              limitsByCurrency={
                preview?.claimMethod === "reimbursement"
                  ? preview.limitsByCurrency
                  : null
              }
              trackingOnly={!isReimbursement}
            />
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
